import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { MessageCircle, Loader2, AlertTriangle } from 'lucide-react';
import api from '../../lib/api';

export const TrialWhatsAppDialog = ({ trial, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [waNumber, setWaNumber] = useState('');
  const [reviewReasons, setReviewReasons] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!trial) return;
    setLoading(true); setError(''); setMessage(''); setReviewReasons([]);
    api.get(`/admin/trial-comms/whatsapp/${trial.id}`)
      .then(res => {
        setMessage(res.data.message || '');
        setWaNumber(res.data.wa_number || '');
        setReviewReasons(res.data.review_reasons || []);
      })
      .catch(e => setError(e?.response?.data?.detail || 'Could not generate a message for this booking'))
      .finally(() => setLoading(false));
  }, [trial]);

  if (!trial) return null;
  const fallbackDigits = (trial.parent_phone || '').replace(/\D/g, '').replace(/^0/, '44');

  return (
    <Dialog open={!!trial} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#111] border-[#333] text-white max-w-lg" data-testid="trial-whatsapp-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-[#25D366]" />
            WhatsApp — {trial.student_first_name} {trial.student_last_name}
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#25D366]" /></div>
        ) : error ? (
          <p className="text-red-400 text-sm py-4">{error}</p>
        ) : (
          <>
            {reviewReasons.length > 0 && (
              <div className="flex items-start gap-2 p-3 rounded bg-amber-500/10 border border-amber-500/30 text-xs text-amber-300" data-testid="whatsapp-review-warning">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <div>Needs review before sending: {reviewReasons.join('; ')}</div>
              </div>
            )}
            <p className="text-xs text-white/50">Preview and edit the message, then tap Open WhatsApp. Nothing is sent automatically.</p>
            <Textarea
              value={message}
              onChange={e => setMessage(e.target.value)}
              rows={14}
              className="bg-black/40 border-[#333] text-white text-sm leading-relaxed"
              data-testid="whatsapp-message-textarea"
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={onClose} className="border-[#444] text-white/70">Cancel</Button>
              <Button
                className="bg-[#25D366] hover:bg-[#1faa52] text-black font-semibold"
                onClick={() => window.open(`https://wa.me/${waNumber || fallbackDigits}?text=${encodeURIComponent(message)}`, '_blank')}
                data-testid="open-whatsapp-btn"
              >
                <MessageCircle className="w-4 h-4 mr-2" /> Open WhatsApp
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TrialWhatsAppDialog;
